import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const AdminUsersInner = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedUser, setSelectedUser] = useState(null); // Full profile modal
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchUsers();
  }, [token, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users.");
    } finally { setLoading(false); }
  };

  const handleViewProfile = async (userId) => {
    setFetchingProfile(true);
    try {
      const res = await axios.get(`${API_URL}/admin/users/${userId}/full-profile`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedUser(res.data);
    } catch (err) {
      toast.error("Failed to load full profile.");
    } finally { setFetchingProfile(false); }
  };

  const handleToggleBlock = async (userId, currentStatus, name) => {
    const action = currentStatus ? "unblock" : "block";
    if (!window.confirm(`${action === "block" ? "⛔" : "✅"} ${action === "block" ? "Block" : "Unblock"} "${name}"?`)) return;
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/block`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User "${name}" has been ${action}ed.`);
      fetchUsers();
    } catch (err) {
      toast.error(`Failed to ${action} user.`);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`⚠️ PERMANENTLY DELETE user "${userName}"?\n\nThis will erase all their teams, contests, and transactions.`)) return;
    const confirmation = window.prompt("Type DELETE to confirm:");
    if (confirmation !== "DELETE") { toast.info("Deletion cancelled."); return; }
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User "${userName}" permanently deleted.`);
      fetchUsers();
    } catch (err) {
      toast.error("Failed to delete user: " + (err.response?.data?.error || err.message));
    }
  };

  const filteredUsers = users
    .filter(u => {
      const nameMatch = (u.first_name + " " + (u.last_name || "")).toLowerCase().includes(search.toLowerCase());
      const mobileMatch = (u.mobile || "").includes(search);
      const emailMatch = (u.email || "").toLowerCase().includes(search.toLowerCase());
      const textMatch = !search || nameMatch || mobileMatch || emailMatch;
      const statusMatch = filterStatus === "ALL" || (filterStatus === "BLOCKED" && u.is_blocked) || (filterStatus === "ACTIVE" && !u.is_blocked);
      return textMatch && statusMatch;
    })
    .sort((a, b) => {
      if (sortBy === "newest") return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === "oldest") return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === "wallet_high") return Number(b.wallet || 0) - Number(a.wallet || 0);
      if (sortBy === "wallet_low") return Number(a.wallet || 0) - Number(b.wallet || 0);
      return 0;
    });

  const totalWallet = users.reduce((s, u) => s + Number(u.wallet || 0), 0);
  const blockedCount = users.filter(u => u.is_blocked).length;

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>👤 User Management</div>
          <div style={S.pageSubtitle}>All Registered Users</div>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {[
            { label: "Total Users", value: users.length, color: COLORS.indigo },
            { label: "Active", value: users.length - blockedCount, color: COLORS.green },
            { label: "Blocked", value: blockedCount, color: COLORS.red },
            { label: "Wallet Pool", value: `₹${totalWallet.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, color: COLORS.amber },
          ].map(stat => (
            <div key={stat.label} style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "12px", padding: "10px 16px", textAlign: "center" }}>
              <div style={{ fontSize: "20px", fontWeight: 800, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: "9px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
        <input
          className="admin-input-focus"
          style={{ ...S.input, maxWidth: "300px", height: "42px" }}
          placeholder="🔍 Search by name, mobile, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...S.select, width: "140px", height: "42px" }}>
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="BLOCKED">Blocked</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.select, width: "180px", height: "42px" }}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="wallet_high">Wallet (High)</option>
          <option value="wallet_low">Wallet (Low)</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: "13px", color: COLORS.textMuted }}>
          Showing <strong style={{ color: COLORS.text }}>{filteredUsers.length}</strong> of {users.length}
        </div>
      </div>

      {loading ? (
        <div style={S.loading}>Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <div style={S.empty}>No users found matching your criteria.</div>
      ) : (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
              <thead>
                <tr style={{ background: COLORS.inputBg }}>
                  {["ID", "User", "Mobile", "Email", "Wallet", "Joined", "Status", "Actions"].map(h => (
                    <th key={h} style={{
                      padding: "14px 16px",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: COLORS.textMuted,
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      textAlign: h === "Actions" ? "center" : "left",
                      borderBottom: `1px solid ${COLORS.cardBorder}`,
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, idx) => (
                  <tr
                    key={u.id}
                    className="admin-row-hover"
                    style={{
                      borderBottom: `1px solid ${COLORS.cardBorder}`,
                      opacity: u.is_blocked ? 0.65 : 1,
                      transition: "background 0.1s",
                      background: idx % 2 === 0 ? COLORS.card : COLORS.surface,
                    }}
                  >
                    <td style={{ padding: "14px 16px", fontSize: "12px", color: COLORS.textMuted }}>{u.id}</td>
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: COLORS.text }}>
                        {u.first_name} {u.last_name || ""}
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: "13px", color: COLORS.textMid }}>{u.mobile || "—"}</td>
                    <td style={{ padding: "14px 16px", fontSize: "12px", color: COLORS.textMuted }}>{u.email || "—"}</td>
                    <td style={{ padding: "14px 16px" }}>
                      <span style={{ fontSize: "14px", fontWeight: 700, color: COLORS.green }}>
                        ₹{parseFloat(u.wallet || 0).toFixed(2)}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: "12px", color: COLORS.textMuted }}>
                      {new Date(u.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <span style={{
                        ...S.badge(u.is_blocked ? COLORS.red : COLORS.green),
                        padding: "4px 10px",
                        fontSize: "9px",
                      }}>
                        {u.is_blocked ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center", flexWrap: "wrap" }}>
                        <button
                          className="admin-btn-hover"
                          onClick={() => handleViewProfile(u.id)}
                          style={{
                            padding: "6px 12px",
                            background: COLORS.indigo + "15",
                            color: COLORS.indigoLight,
                            border: `1px solid ${COLORS.indigo}40`,
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          📄 Profile
                        </button>
                        <button
                          className="admin-btn-hover"
                          onClick={() => handleToggleBlock(u.id, u.is_blocked, u.first_name)}
                          style={{
                            padding: "6px 14px",
                            background: u.is_blocked ? COLORS.green + "20" : COLORS.red + "15",
                            color: u.is_blocked ? COLORS.green : COLORS.red,
                            border: `1px solid ${u.is_blocked ? COLORS.green + "40" : COLORS.red + "30"}`,
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: 700,
                            transition: "all 0.15s",
                          }}
                        >
                          {u.is_blocked ? "✅ Unblock" : "⛔ Block"}
                        </button>
                        <button
                          className="admin-btn-hover"
                          onClick={() => handleDeleteUser(u.id, u.first_name + " " + (u.last_name || ""))}
                          style={{
                            padding: "6px 12px",
                            background: "transparent",
                            color: COLORS.textMuted,
                            border: `1px solid ${COLORS.cardBorder}`,
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "11px",
                            transition: "all 0.15s",
                          }}
                          title="Delete user permanently"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FULL PROFILE MODAL */}
      {selectedUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "24px", width: "100%", maxWidth: "600px", maxHeight: "90vh", overflowY: "auto", position: "relative", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)" }}>
            <button onClick={() => setSelectedUser(null)} style={{ position: "absolute", top: "20px", right: "20px", background: "rgba(255,255,255,0.05)", border: "none", color: COLORS.text, fontSize: "20px", cursor: "pointer", width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            
            <div style={{ padding: "32px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "32px" }}>
                <div style={{ width: "64px", height: "64px", borderRadius: "20px", background: COLORS.indigo + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px" }}>👤</div>
                <div>
                  <h2 style={{ fontSize: "24px", fontWeight: 800, margin: 0 }}>{selectedUser.user.first_name} {selectedUser.user.last_name}</h2>
                  <p style={{ color: COLORS.textMuted, margin: "4px 0 0", fontSize: "14px" }}>ID: {selectedUser.user.id} · Joined {new Date(selectedUser.user.created_at).toLocaleDateString("en-IN")}</p>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "32px" }}>
                <div style={{ background: COLORS.surface, padding: "16px", borderRadius: "16px", border: `1px solid ${COLORS.cardBorder}` }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: "8px" }}>Wallet Balance</div>
                  <div style={{ fontSize: "24px", fontWeight: 800, color: COLORS.green }}>₹{Number(selectedUser.user.wallet).toLocaleString("en-IN")}</div>
                </div>
                <div style={{ background: COLORS.surface, padding: "16px", borderRadius: "16px", border: `1px solid ${COLORS.cardBorder}` }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: "8px" }}>Phone Number</div>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: COLORS.text }}>{selectedUser.user.mobile}</div>
                </div>
              </div>

              <h3 style={{ fontSize: "14px", fontWeight: 700, textTransform: "uppercase", color: COLORS.indigoLight, letterSpacing: "1px", marginBottom: "16px", borderBottom: `1px solid ${COLORS.cardBorder}`, paddingBottom: "10px" }}>🏦 Bank Account Details</h3>
              {selectedUser.bank ? (
                <div style={{ background: COLORS.indigo + "08", border: `1px solid ${COLORS.indigo}20`, borderRadius: "16px", padding: "20px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                    <div>
                      <div style={{ fontSize: "10px", color: COLORS.textMuted, marginBottom: "4px" }}>ACCOUNT NUMBER</div>
                      <div style={{ fontSize: "15px", fontWeight: 700 }}>{selectedUser.bank.account_number}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", color: COLORS.textMuted, marginBottom: "4px" }}>IFSC CODE</div>
                      <div style={{ fontSize: "15px", fontWeight: 700 }}>{selectedUser.bank.ifsc}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", color: COLORS.textMuted, marginBottom: "4px" }}>BANK NAME</div>
                      <div style={{ fontSize: "14px", fontWeight: 600 }}>{selectedUser.bank.bank_name || "—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", color: COLORS.textMuted, marginBottom: "4px" }}>HOLDER NAME</div>
                      <div style={{ fontSize: "14px", fontWeight: 600 }}>{selectedUser.bank.holder_name}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ background: COLORS.red + "08", border: `1px solid ${COLORS.red}20`, borderRadius: "16px", padding: "20px", textAlign: "center", color: COLORS.red, fontSize: "13px", fontWeight: 600 }}>
                  ⚠️ User has not added bank details yet.
                </div>
              )}

              <div style={{ marginTop: "32px", textAlign: "center" }}>
                <button onClick={() => setSelectedUser(null)} style={{ ...S.btnCancel, width: "100%", padding: "14px" }}>Close Profile</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminUsers = () => (
  <ToastProvider>
    <AdminUsersInner />
  </ToastProvider>
);

export default AdminUsers;
